export type ServiceFaq = { q: string; a: string };

/** Questions that apply to every engagement, appended to each service page. */
const sharedFaqs: ServiceFaq[] = [
  {
    q: "How is this priced?",
    a: "Hourly, by seniority: $6/hr for a junior bookkeeper, $9/hr for an accountant, and $12/hr for a senior accountant or controller. Full-time cover of 160 hours a month works out to $960, $1,440, and $1,920 respectively. You only pay for hours worked, billed monthly on Net 7 terms with no lock-in beyond a 30-day notice period.",
  },
  {
    q: "How quickly can we start?",
    a: "Discovery and access setup take 1–3 business days. Most clients are in steady state within two to four weeks, faster when the books are already current and longer when historical catch-up is needed.",
  },
  {
    q: "How do you keep our financial data secure?",
    a: "Access is least-privilege and role-based, data is encrypted in transit and at rest, every team member is under NDA, and we work inside your systems rather than exporting data to ours. You can revoke access at any time.",
  },
];

const bySlug: Record<string, ServiceFaq[]> = {
  bookkeeping: [
    {
      q: "How far behind can our books be before you take them on?",
      a: "There is no limit. We regularly rebuild two or three years of history. Catch-up runs as a separate 2–4 week workstream so the live monthly cycle starts clean from day one.",
    },
    {
      q: "Do you work in our accounting software or your own?",
      a: "Yours. We work directly in QuickBooks Online or Xero with read/write access scoped to what the work requires, plus tools like Dext, Hubdoc, Ramp, or Brex where you already use them.",
    },
    {
      q: "Who owns the chart of accounts?",
      a: "You do. We review and propose a tightened structure during onboarding so reporting is usable, but nothing changes without your sign-off.",
    },
    {
      q: "How often are transactions actually recorded?",
      a: "Daily. New bank and card activity is coded within one business day of feed sync, with a weekly digest of anything that needs your input.",
    },
  ],
  payroll: [
    {
      q: "Do you file payroll taxes on our behalf?",
      a: "We prepare and process payroll inside your provider (Gusto, ADP, Paychex, Rippling), reconcile the tax liabilities, and confirm filings. Registered filing agent responsibility stays with your provider or CPA.",
    },
    {
      q: "Can you handle multi-state and global payroll?",
      a: "Yes. We manage multi-state withholding setup, international jurisdiction rules, and the reconciliation of payroll liabilities across each pay run.",
    },
    {
      q: "What's the cut-off for submitting hours?",
      a: "Two business days before pay date. That gives us time to validate hours, review exceptions, and get your approval before anything is submitted.",
    },
    {
      q: "Do you handle contractors, W-9s, and global compliance?",
      a: "Yes — contractor payments, W-9 and international tax form collection, and year-end reporting (including 1099s) are all part of the cycle.",
    },
  ],
  "accounts-payable": [
    {
      q: "Do you have authority to release payments?",
      a: "No. We prepare, code, and queue payment batches; final release always sits with an approver on your side. Approval workflows are configured to your policy during onboarding.",
    },
    {
      q: "How do you prevent duplicate or fraudulent invoices?",
      a: "Every invoice is matched against the PO or contract, checked against vendor master data, and screened for duplicates before it enters an approval queue. Bank-detail changes require out-of-band verification.",
    },
    {
      q: "Can you manage vendor communication?",
      a: "Yes. We can own the AP inbox, chase missing invoices and W-9s, and respond to vendor payment status questions under your brand.",
    },
    {
      q: "How quickly are invoices processed?",
      a: "Within one business day of receipt. Weekly payment runs are prepared on your chosen schedule with a full batch summary before release.",
    },
  ],
  "accounts-receivable": [
    {
      q: "Will you contact our customers directly?",
      a: "Only if you want us to. Many clients have us send reminders and statements from a shared inbox under their brand; others prefer we prepare the outreach for their team to send.",
    },
    {
      q: "How do you handle collections escalation?",
      a: "We follow an agreed dunning ladder — reminder, statement, escalation — and flag accounts for your decision before anything moves to a hard collections step.",
    },
    {
      q: "Can you improve our DSO?",
      a: "Typically yes. Consistent invoicing, same-day cash application, and a disciplined follow-up cadence are what move DSO; most clients see meaningful improvement within one or two quarters.",
    },
    {
      q: "Do you apply cash and handle short payments?",
      a: "Yes. Receipts are applied daily, deductions and short payments are documented, and disputes are logged with the context your team needs to resolve them.",
    },
  ],
  "month-end-close": [
    {
      q: "How fast is your close?",
      a: "Five business days is the standard cadence, and we can compress further once the process is stable. Day 1–3 covers reconciliations and accruals, day 4 review, day 5 delivery of the reporting pack.",
    },
    {
      q: "What's in the monthly reporting pack?",
      a: "P&L, balance sheet, and cash flow with prior-period and budget comparisons, a reconciliation summary, supporting schedules, and a written variance commentary.",
    },
    {
      q: "Do you work with our CPA or auditor?",
      a: "Yes. We maintain an audit-ready documentation trail and respond to PBC lists and auditor questions directly, so your team isn't the bottleneck.",
    },
    {
      q: "What if our books aren't clean enough to close?",
      a: "We run a cleanup pass first, then start the close cadence. That sequencing is scoped and priced up front so there are no surprises.",
    },
  ],
  accounting: [
    {
      q: "Is this cash or accrual accounting?",
      a: "Either. Most growing companies move to accrual for accurate margin and revenue timing, and we can run the conversion as part of onboarding.",
    },
    {
      q: "Can you support revenue recognition schedules?",
      a: "Yes — deferred revenue, prepaid amortization, and multi-period contract schedules are maintained monthly with supporting workpapers.",
    },
    {
      q: "Do you prepare our tax returns?",
      a: "We prepare the books, schedules, and year-end package your CPA needs. Filing stays with your tax preparer, and we coordinate directly with them.",
    },
    {
      q: "Who reviews the work?",
      a: "Every engagement has a named preparer plus a senior reviewer. Nothing is delivered without a second set of eyes.",
    },
  ],
  reconciliations: [
    {
      q: "Which accounts do you reconcile?",
      a: "All bank, credit card, merchant, payment processor, and loan accounts, plus intercompany and clearing accounts where applicable.",
    },
    {
      q: "What happens when a reconciliation doesn't balance?",
      a: "The variance is investigated, documented with supporting evidence, and either corrected or escalated to you with a recommendation — never left as a plug entry.",
    },
    {
      q: "Do you reconcile Stripe, PayPal, Shopify, and Amazon?",
      a: "Yes. Processor and marketplace settlements — including fees, refunds, chargebacks, and holdbacks — are reconciled to the deposits that hit your bank.",
    },
    {
      q: "How often are reconciliations done?",
      a: "Monthly as standard, completed by business day 3. High-volume accounts can be reconciled weekly or daily.",
    },
  ],
  "financial-reporting": [
    {
      q: "Can reports be customized to our KPIs?",
      a: "Yes. Alongside the standard statements we build department, location, or product-level views and track the operating metrics your leadership actually reviews.",
    },
    {
      q: "Do you provide commentary or just the numbers?",
      a: "Both. Every pack includes written variance commentary explaining what moved, why, and what to watch next period.",
    },
    {
      q: "Can you produce investor or board reporting?",
      a: "Yes — board decks, investor updates, and lender reporting packages are all within scope, prepared to your template and timeline.",
    },
    {
      q: "How do you handle budget vs. actual?",
      a: "We load your budget or forecast into the reporting model and report actuals against it monthly, with drill-down to transaction level for any material variance.",
    },
  ],
  "cfo-controller-support": [
    {
      q: "Is this a fractional CFO or a controller?",
      a: "Both are available. Controller support covers close oversight, controls, and reporting quality; CFO support adds forecasting, scenario planning, fundraising and lender support, and board-level guidance.",
    },
    {
      q: "How many hours a month do companies typically need?",
      a: "Most start between 10 and 40 hours a month depending on complexity. We scope from your actual calendar of decisions rather than selling a fixed retainer.",
    },
    {
      q: "Can you help with fundraising or a lending process?",
      a: "Yes — data room preparation, historical financial packages, model review, and diligence question response are all standard.",
    },
    {
      q: "Will they join our leadership meetings?",
      a: "Yes. Your controller or CFO joins recurring leadership or board calls on an agreed cadence.",
    },
  ],
  "quickbooks-xero": [
    {
      q: "Can you migrate us from one platform to another?",
      a: "Yes. QuickBooks to Xero and Xero to QuickBooks migrations include chart mapping, historical data transfer, opening balance validation, and a parallel-run check before cutover.",
    },
    {
      q: "Do you set up integrations with our other systems?",
      a: "Yes — payments, payroll, expense, e-commerce, CRM, and inventory apps are connected and mapped so data flows without manual re-keying.",
    },
    {
      q: "Can you fix a file that's already a mess?",
      a: "That's a common starting point. We rebuild the chart of accounts, clear undeposited funds and suspense balances, fix mis-postings, and re-reconcile affected periods.",
    },
    {
      q: "Will you train our internal team?",
      a: "Yes. We document the workflows and run walkthrough sessions so your team can operate the day-to-day confidently.",
    },
  ],
  "catch-up-cleanup": [
    {
      q: "How long does a catch-up take?",
      a: "Two to four weeks for most engagements. Multi-year rebuilds or heavy transaction volume can run six to eight weeks; we give you a period-by-period schedule before starting.",
    },
    {
      q: "What do you need from us to start?",
      a: "Access to the accounting file, bank and card statements for the affected periods, and any payroll and loan documentation. We work from source documents wherever possible.",
    },
    {
      q: "Will this make us filing-ready?",
      a: "Yes — that's the goal. You end with reconciled periods, corrected balances, supporting schedules, and a year-end package your CPA can file from.",
    },
    {
      q: "What happens after the cleanup is done?",
      a: "You can stop there or roll into the ongoing bookkeeping and close cadence so the books never drift again. There's no obligation either way.",
    },
  ],
  "finance-process-outsourcing": [
    {
      q: "What does a full finance pod include?",
      a: "A blended team — bookkeeper, accountant, and senior reviewer or controller — covering transaction processing, AP and AR, payroll, close, and reporting under one engagement.",
    },
    {
      q: "How is this different from hiring in-house?",
      a: "You get a team with built-in coverage and review instead of a single hire, at a fraction of loaded cost, and you can scale hours up or down as volume changes.",
    },
    {
      q: "Can we keep some functions in-house?",
      a: "Yes. Most clients keep approvals and treasury internal while outsourcing processing and close. We map the split explicitly during onboarding.",
    },
    {
      q: "What happens if a team member is unavailable?",
      a: "Every engagement is documented and staffed with a reviewer who knows the account, so coverage continues without a handover gap.",
    },
  ],
};

export function faqsForService(slug: string): ServiceFaq[] {
  return [...(bySlug[slug] ?? []), ...sharedFaqs];
}
